import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Col, Alert } from "react-bootstrap";
import Slider from "../../components/Slider";

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/fetch-categories`
        );
        const parentCategories = response.data.data.terms.filter(
          (cat) => cat.parent_id === null
        );
        setCategories(parentCategories);
      } catch (err) {
        setError("Unable to load categories");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <div className="min-vh-50 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div
            className="spinner-border text-primary mb-3"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mb-0">Loading categories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-5">
        <Container>
          <Alert
            variant="light"
            className="border-0 bg-light rounded-4 text-center py-4"
          >
            <i className="bi bi-exclamation-circle text-warning fs-2 mb-3 d-block"></i>
            <h5 className="mb-2">Something went wrong</h5>
            <p className="text-muted mb-0">{error}</p>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <>
      {/* Hero Slider Section */}
      <section className="mb-0">
        <Slider />
      </section>

      {/* Categories Section */}
      <section className="py-5 bg-light">
        <Container>
          {/* Header */}
          <div className="text-center mb-5">
            <h1 className="display-5 fw-light text-dark mb-3">
              Learning Categories
            </h1>
            <div className="mx-auto" style={{ maxWidth: "600px" }}>
              <p className="lead text-muted mb-0">
                Discover structured learning paths designed to advance your
                skills
              </p>
            </div>
          </div>

          {/* Categories Grid */}
          {categories.length > 0 ? (
            <Row className="g-4">
              {categories.map((category) => (
                <Col key={category.id} xs={12} sm={6} lg={4} xl={3}>
                  <a
                    href={`/learn/${category.slug}`}
                    className="text-decoration-none"
                  >
                    <div
                      className="card border-0 shadow-sm h-100 overflow-hidden"
                      style={{
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-8px)";
                        e.currentTarget.style.boxShadow =
                          "0 20px 40px rgba(0,0,0,0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "";
                      }}
                    >
                      {/* Color accent bar */}
                      <div
                        style={{
                          height: "4px",
                          background: `linear-gradient(90deg, ${
                            category.color || "#6366f1"
                          }, ${category.color || "#6366f1"}99)`,
                        }}
                      />

                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between mb-3">
                          <div className="flex-grow-1 pe-3">
                            <h3
                              className="h5 mb-0 fw-medium"
                              style={{
                                color: "#1a1a1a",
                                lineHeight: "1.3",
                              }}
                            >
                              {category.name}
                            </h3>
                          </div>

                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: "48px",
                              height: "48px",
                              backgroundColor: `${
                                category.color || "#6366f1"
                              }15`,
                              color: category.color || "#6366f1",
                            }}
                          >
                            <i className="bi bi-journals fs-5"></i>
                          </div>
                        </div>

                        <p
                          className="text-muted mb-0 small"
                          style={{
                            lineHeight: "1.5",
                            display: "-webkit-box",
                            WebkitLineClamp: "3",
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {category.description}
                        </p>
                      </div>

                      {/* Hover indicator */}
                      <div
                        className="position-absolute bottom-0 end-0 p-3 opacity-0"
                        style={{
                          transition: "opacity 0.3s ease",
                          color: category.color || "#6366f1",
                        }}
                        onMouseEnter={(e) => {
                          const card = e.currentTarget.closest(".card");
                          if (card) {
                            e.currentTarget.style.opacity = "1";
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = "0";
                        }}
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M7 17L17 7M17 7H7M17 7V17" />
                        </svg>
                      </div>
                    </div>
                  </a>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center py-5">
              <div
                className="mx-auto mb-4 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "#f8fafc",
                }}
              >
                <i
                  className="bi bi-collection text-muted"
                  style={{ fontSize: "2rem" }}
                ></i>
              </div>
              <h4 className="fw-light text-dark mb-2">No Categories Yet</h4>
              <p className="text-muted mb-0">
                Learning categories are being prepared and will be available
                soon.
              </p>
            </div>
          )}

          {/* Footer stats */}
          {categories.length > 0 && (
            <div className="text-center mt-5 pt-4 border-top border-light">
              <small className="text-muted">
                {categories.length} learning categor
                {categories.length !== 1 ? "ies" : "y"} available
              </small>
            </div>
          )}
        </Container>
      </section>
    </>
  );
};

export default CategoryList;
